import React from 'react';
import { useStyletron } from 'baseui';
import { Button } from '@letsdothis/lds/lib/Button';

import { useAuthService } from '@/contexts/authService';
import { logError } from '@/utils/logError';

import FieldValidators from '../FieldValidators';
import * as styles from '../styles.module.css';
import { STAGES } from '../../enums';
import Field from '../../../Form/Fields/Field';
import { ConfirmForgotPasswordFormParams } from './Types';

const confirmForgotPassword = async (
  authService: any,
  props: ConfirmForgotPasswordFormParams,
) => {
  const {
    confirmationCode,
    email,
    password,
    previousPasswordError,
    setState,
  } = props;
  const passwordError = FieldValidators.validatePassword(password);
  if (passwordError !== previousPasswordError) {
    setState({ passwordError });
    throw new Error('Invalid password');
  }
  if (!passwordError) {
    await authService.confirmForgotPassword({
      confirmationCode,
      email,
      password,
    });
  }
};

function ConfirmForgotPasswordForm(
  props: ConfirmForgotPasswordFormParams,
): JSX.Element {
  const authService = useAuthService();
  const [css, theme] = useStyletron();

  const {
    confirmationCode,
    password,
    passwordError,
    setStage,
    setState,
    signInOrUpButtonLoading,
    signInOrUpError,
  } = props;

  const setConfirmationCode = (code?: string) =>
    setState({ confirmationCode: code });

  const setNewPassword = (newPassword?: string) =>
    setState({ password: newPassword });

  return (
    <div className={styles.emailPasswordContainer}>
      <h1>We've sent you a confirmation code. Please check your email.</h1>
      {Field({
        title: 'Confirmation Code',
        value: confirmationCode,
        setValue: setConfirmationCode,
      })}
      {Field({
        title: 'New Password',
        hidden: true,
        value: password,
        setValue: setNewPassword,
        error: passwordError,
      })}
      <Button
        isLoading={signInOrUpButtonLoading}
        onClick={() => {
          setState({ signInOrUpButtonLoading: true });
          setState({ signInOrUpError: undefined });
          confirmForgotPassword(authService, props)
            .then(() => {
              setState({ signInOrUpButtonLoading: false });
              setState({ recoverPasswordSuccess: false });
              setState({ forgotPassword: false });
              setState({ password });
              setState({ signInOrUpError: undefined });
              setStage(STAGES.LOGIN);
            })
            .catch((error) => {
              setState({ signInOrUpButtonLoading: false });
              setState({
                signInOrUpError: 'Something went wrong.  Please try again.',
              });
              logError(error, {
                errorMessageFallback: 'Error while confirming new password.',
              });
            });
        }}
      >
        Confirm New Password
      </Button>
      {signInOrUpError && (
        <div className={css({ color: theme.colors.negative400 })}>
          {signInOrUpError}
        </div>
      )}
    </div>
  );
}

export default ConfirmForgotPasswordForm;
